import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, Mail, Phone, MapPin, ArrowUpRight } from "lucide-react";
import InfinityMark from "./Logo";

const NAV = [
  { to: "/our-service", label: "Our Service", id: "our-service" },
  { to: "/outreach-service", label: "Outreach", id: "outreach-service" },
  { to: "/young-kings-programme", label: "Young Kings", id: "young-kings" },
  { to: "/care-support", label: "Care & Support", id: "care-support" },
  { to: "/local-area", label: "Local Area", id: "local-area" },
  { to: "/education-outcomes", label: "Education", id: "education-outcomes" },
  { to: "/team", label: "Our Team", id: "team" },
];

function Header() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-[70] border-b border-line bg-white/95 backdrop-blur-xl"
    >
      <div className="container-x flex h-[72px] items-center justify-between gap-4">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-3">
          <InfinityMark className="h-10 w-10" id="nav" />
          <span className="text-[11px] font-extrabold uppercase leading-[1.3] tracking-[0.08em] text-[#17372E]">
            Infinity and Beyond Care
            <br />
            EBD Home
          </span>
        </Link>

        <nav className="hidden items-center gap-6 lg:flex" data-testid="nav-desktop">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-link-${item.id}`}
              className={({ isActive }) =>
                `whitespace-nowrap text-[13px] font-bold transition-colors duration-150 hover:text-[#2B8A78] ${
                  isActive
                    ? "text-[#2B8A78] underline decoration-2 underline-offset-[6px]"
                    : "text-[#3E5348]"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <Link
            to="/referrals"
            data-testid="nav-cta-referrals"
            className="btn whitespace-nowrap bg-[#2B8A78] !px-5 !py-2.5 text-[13px] text-white hover:-translate-y-0.5 hover:bg-[#237464]"
          >
            Make a Referral
            <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
          </Link>
        </nav>

        <button
          type="button"
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
          onClick={() => setOpen((v) => !v)}
          className="flex h-11 w-11 items-center justify-center rounded-full border border-line bg-white text-[#17372E] lg:hidden"
        >
          {open ? <X className="h-5 w-5" strokeWidth={1.5} /> : <Menu className="h-5 w-5" strokeWidth={1.5} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="border-t border-line bg-white px-5 pb-8 pt-4 lg:hidden"
        >
          <nav className="flex flex-col">
            <NavLink
              to="/"
              data-testid="nav-mobile-link-home"
              className="border-b border-line/70 py-4 text-lg font-bold text-[#17372E]"
              onClick={() => setOpen(false)}
            >
              Home
            </NavLink>
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-mobile-link-${item.id}`}
                className="border-b border-line/70 py-4 text-lg font-bold text-[#17372E]"
                onClick={() => setOpen(false)}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
          <Link
            to="/referrals"
            data-testid="nav-mobile-cta"
            className="btn mt-6 w-full bg-[#2B8A78] text-white"
            onClick={() => setOpen(false)}
          >
            Make a Referral
            <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
          </Link>
        </div>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer data-testid="site-footer" className="relative z-10 mt-0 bg-[#17372E] text-white/80">
      <div className="edge-rainbow absolute inset-x-0 top-0" aria-hidden="true" />
      <div className="container-x flex flex-wrap items-center justify-between gap-8 py-10">
        <div className="flex items-center gap-3" data-testid="footer-logo">
          <InfinityMark className="h-11 w-11" id="footer" />
          <span className="text-[11px] font-extrabold uppercase leading-[1.3] tracking-[0.08em] text-white">
            Infinity and Beyond Care
            <br />
            EBD Home
          </span>
        </div>

        <div className="flex items-start gap-3 text-sm" data-testid="footer-address">
          <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-sun" strokeWidth={1.5} />
          <span>
            81 Bicester Road,
            <br />
            Aylesbury, Buckinghamshire, HP19 9AN
          </span>
        </div>

        <div className="flex flex-col gap-2 text-sm" data-testid="footer-contact">
          <a href="tel:+447748433226" data-testid="footer-phone" className="flex items-center gap-3 font-bold text-white transition-colors hover:text-sun">
            <Phone className="h-4 w-4 shrink-0 text-sun" strokeWidth={1.5} />
            +44 7748 433226
          </a>
          <a
            href="mailto:info@infinityandbeyonccare.co.uk"
            data-testid="footer-email"
            className="flex items-center gap-3 break-all font-bold text-white transition-colors hover:text-sun"
          >
            <Mail className="h-4 w-4 shrink-0 text-sun" strokeWidth={1.5} />
            info@infinityandbeyonccare.co.uk
          </a>
        </div>

        <div className="text-sm leading-snug text-white/70" data-testid="footer-tagline">
          One home. One child.
          <br />A consistent team around him.
        </div>
      </div>
    </footer>
  );
}

const BG_MARKS = [
  { style: { top: "6%", left: "-4%", transform: "rotate(-12deg)" }, className: "h-64 w-auto opacity-[0.05]" },
  { style: { top: "28%", right: "-5%", transform: "rotate(10deg)" }, className: "h-72 w-auto opacity-[0.04]" },
  { style: { top: "52%", left: "3%", transform: "rotate(8deg)" }, className: "h-56 w-auto opacity-[0.05]" },
  { style: { top: "72%", right: "6%", transform: "rotate(-8deg)" }, className: "h-64 w-auto opacity-[0.04]" },
  { style: { top: "90%", left: "28%", transform: "rotate(14deg)" }, className: "h-52 w-auto opacity-[0.05]" },
];

export default function Layout() {
  return (
    <div className="flex min-h-screen flex-col bg-cream">
      <div aria-hidden="true" className="pointer-events-none fixed inset-0 z-0 overflow-hidden">
        {BG_MARKS.map((mark, i) => (
          <img
            key={i}
            src="/logo-mark.png"
            alt=""
            className={`absolute ${mark.className}`}
            style={mark.style}
          />
        ))}
      </div>
      <Header />
      <main className="relative z-10 flex-1">
        <Outlet />
      </main>
      <Footer />
      <div className="grain" aria-hidden="true" />
    </div>
  );
}
